import { StylesConfig } from "react-select";
import { cubeCustomColors } from "../../../tailwind.config";

export function reactSelectCustomStylesSelectPanel(theme: string | undefined) {
  const stylesToReturn: StylesConfig<any> = {
    //Load all dark colours first
    control: (base) => ({
      ...base,
      backgroundColor: theme == "dark" ? "#222222" : "", // dark background color
      color: theme == "dark" ? "white" : "black", // Light text color
      borderRadius: "10px",
      fontSize: "10px", // Font size of the text
      // padding: "2px 8px", // Smaller padding to reduce the overall size of the select box
      height: "30px", // Reduce the height of the select box
      minHeight: "30px", // Ensure the minimum height is also smaller
      width: "160px",
      display: "flex", // Use flexbox to align items properly
      alignItems: "center", // Vertically center the text and the dropdown indicator
      lineHeight: "normal", // Ensures proper line height for the text
    }),
    singleValue: (base) => ({
      ...base,
      color: theme == "dark" ? "white" : "black", // Set text color to white
      fontSize: "10px", // Font size of the selected value
      marginTop: 0, // Ensure the selected value is aligned vertically
      paddingTop: 0, // Prevents extra space above the selected value
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "10px", // Font size of the options
      padding: "7px", // Adjust padding for the options
      lineHeight: "normal", // Proper line height for options
      backgroundColor: state.isFocused
        ? theme === "dark"
          ? cubeCustomColors.cubeBaseDark[500]
          : cubeCustomColors.cubeBase[700]
        : theme === "dark"
          ? "#303739" // Dark theme background color when not focused
          : "#F2F8FC", // Light theme background color when not focused
      color: state.isSelected
        ? theme == "dark"
          ? "#0EA5E9"
          : "#0EA5E9"
        : "inherit", // Use white color for selected option
    }),
    dropdownIndicator: (base) => ({
      ...base,
      fontSize: "10px", // Smaller dropdown arrow
      padding: 0, // Remove padding to reduce size
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: "none", // Optional: Remove the separator line
    }),

    clearIndicator: (base) => ({
      ...base,
      fontSize: "10px", // Smaller clear "X" button
      padding: "0", // Reduce padding to make the clear button compact
      display: "flex", // Use flex to align the button
      alignItems: "center", // Vertically center the clear button
      justifyContent: "center", // Horizontally center the clear button
    }),

    noOptionsMessage: (provided) => ({
      ...provided,
      color: "red", // Customize the color of the "no options" message
      fontSize: "10px",
      padding: "1px",
      backgroundColor:
        theme === "dark"
          ? "#303739" // Dark theme background color when not focused
          : "#F2F8FC", // Light theme background color when not focused
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),

    input: (provided) => ({
      ...provided,
      color: "white", // Change this to your desired color
      height: "20px",
      width: "100%",
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor:
        theme === "dark"
          ? "#222222" // Dark theme background color when not focused
          : "rgba(255, 255, 255, 1)", // Light theme background color when not focused
    }),
  };
  return stylesToReturn;
}

// Styles that match the native <select> elements used elsewhere in the panel
// (h-[28px] rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800).
export function reactSelectNativeStyle(theme: string | undefined) {
  const isDark = theme === "dark";
  const stylesToReturn: StylesConfig<any> = {
    control: (base) => ({
      ...base,
      backgroundColor: isDark ? "#1f2937" : "transparent",
      borderColor: isDark ? "#4b5563" : "#d1d5db",
      borderRadius: "6px",
      height: "28px",
      minHeight: "28px",
      boxShadow: "none",
      "&:hover": { borderColor: isDark ? "#4b5563" : "#d1d5db" },
    }),
    valueContainer: (base) => ({
      ...base,
      height: "28px",
      padding: "0 6px",
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? "#e5e7eb" : "inherit",
      fontSize: "12px",
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? "#6b7280" : "#9ca3af",
      fontSize: "12px",
    }),
    input: (base) => ({
      ...base,
      color: isDark ? "#e5e7eb" : "inherit",
      margin: 0,
      padding: 0,
      fontSize: "12px",
    }),
    // Keep the clear (X) and dropdown arrow tight against each other at the
    // right edge — minimal padding between them, small gap from the border.
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0 4px 0 0",
      color: isDark ? "#9ca3af" : "#6b7280",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    clearIndicator: (base) => ({
      ...base,
      padding: 0,
      color: isDark ? "#9ca3af" : "#6b7280",
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "12px",
      padding: "7px",
      backgroundColor: state.isFocused
        ? isDark
          ? cubeCustomColors.cubeBaseDark[500]
          : cubeCustomColors.cubeBase[700]
        : isDark
          ? "#303739"
          : "#F2F8FC",
      color: state.isSelected ? "#0EA5E9" : "inherit",
    }),
    menuList: (base) => ({
      ...base,
      backgroundColor: isDark ? "#222222" : "rgba(255, 255, 255, 1)",
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: "red",
      fontSize: "12px",
      padding: "1px",
      backgroundColor: isDark ? "#303739" : "#F2F8FC",
    }),
    // As noOptionsMessage, minus the red: still loading is not a miss.
    loadingMessage: (base) => ({
      ...base,
      color: isDark ? "#9ca3af" : "#6b7280",
      fontSize: "12px",
      padding: "1px",
      backgroundColor: isDark ? "#303739" : "#F2F8FC",
    }),
    loadingIndicator: (base) => ({
      ...base,
      padding: "0 4px",
      color: isDark ? "#9ca3af" : "#6b7280",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };
  return stylesToReturn;
}
